import React, { createContext } from 'react';

import difference from 'lodash/difference';

import type { CustomizationItem } from '../../common/types';
import { createDefaultCustomizationItems } from '../../common/utils/create-default-customization-items';
import { getMenuIdsByVisible } from '../../common/utils/get-menu-ids-by-visible';
import { repairItems } from '../../common/utils/repair-items';

import type { SideNavContextType, SideNavDataProviderProps, SideNavMenuData } from './types';

export const SideNavContext = createContext<SideNavContextType | null>(null);

/**
 * SideNavDataProvider.
 * @param children - The children components that will have access to the sidenav data.
 * @param data - The data to be provided to the children components.
 *
 * This includes the sidebar customization data, app items, and app shortcuts.
 * @experimental
 * Still under development. Use with caution.
 */
export function SideNavDataProvider({ children, data }: SideNavDataProviderProps) {
	return <SideNavContext.Provider value={data}>{children}</SideNavContext.Provider>;
}

/**
 * Builds the sideNavMenuData.
 * TODO: Decide whether we really need the data provider (above) or not.
 * TODO: If this is just logic, it probably belongs in a service?
 */
export const buildSideNavMenuData = (
	defaultItemMenuIds: string[],
	defaultShortcutMenuIds: string[],
	rawCustomizationData: CustomizationItem[] | null | undefined,
	showAllItems: boolean = false,
): SideNavMenuData => {
	const userCustomizationData = showAllItems ? [] : rawCustomizationData;
	const repairedDefaultItemMenuIds = difference(defaultItemMenuIds, defaultShortcutMenuIds);

	const itemCustomizationItems = repair(userCustomizationData, repairedDefaultItemMenuIds);
	const shortcutCustomizationItems = repair(userCustomizationData, defaultShortcutMenuIds);
	const customizationItems = itemCustomizationItems.concat(shortcutCustomizationItems);

	const [visibleItemMenuIds, hiddenItemMenuIds] = getMenuIdsByVisible(itemCustomizationItems);
	const [visibleShortcutMenuIds, hiddenShortcutMenuIds] = getMenuIdsByVisible(
		shortcutCustomizationItems,
	);
	const visibleMenuIds = visibleItemMenuIds.concat(visibleShortcutMenuIds);
	const hiddenMenuIds = hiddenItemMenuIds.concat(hiddenShortcutMenuIds);

	return {
		customizationItems,
		visibleMenuIds,
		hiddenMenuIds,
		itemCustomizationItems,
		visibleItemMenuIds,
		hiddenItemMenuIds,
		shortcutCustomizationItems,
		visibleShortcutMenuIds,
		hiddenShortcutMenuIds,
	};
};

const repair = (
	rawCustomizationData: CustomizationItem[] | null | undefined,
	defaultMenuIds: string[],
) => repairItems(rawCustomizationData ?? [], createDefaultCustomizationItems(defaultMenuIds));
